'use client';

import { SideId } from '@wareena/grid-battles-entity/game/codebook/side';
import { notFound } from 'next/navigation';
import { Scenario } from '@wareena/grid-battles-entity/scenario/entity/scenario';
import { GameState, Hex, Unit } from '@wareena/grid-battles-engine/gameStateMachine/types';
import { Game } from '@wareena/grid-battles-engine/entity/game';
import GameBoard from '@wareena/grid-battles-ui/game/map/GameBoard';
import { StrategyRecipeIngredient } from '@wareena/grid-battles-engine/agent/strategy/types';
import { StateRequestShape } from '@wareena/grid-battles-engine/gameStateMachine/types';
import Navigation from '@local/game/ui/Navigation';
import { GameContextProvider } from '@wareena/grid-battles-ui/game/map/GameBoard/GameContext';
import { useEffect, useState } from 'react';

const strategyRecipeIngredients: StrategyRecipeIngredient[] = [
    {
        name: 'achieveVictory',
        maxOrders: -1,
    },
    {
        name: 'preventDefeat',
        maxOrders: -1,
    },
    {
        name: 'incurDamage',
        maxOrders: 2,
    },
    {
        name: 'supportFriendlyUnit',
        maxOrders: 1,
    },
    {
        name: 'moveTowardsEnemy',
        maxOrders: 1,
    },
    {
        name: 'supportFriendlyUnit',
        maxOrders: -1,
    },
];

function GamePage({ scenario }: { scenario: Scenario }) {
    const gameId = 123;
    const userId = 42;

    const [isLoading, setIsLoading] = useState(true);
    const initialGame = createGame(scenario, gameId, userId);
    const [game, setGame] = useState<Game>(structuredClone(initialGame));
    const [gameResetId, setGameResetId] = useState(0);

    const [initialAgentRequests, setInitialAgentRequests] = useState<StateRequestShape[]>([]);
    const saveId = `s${scenario.id}-g${gameId}`;

    useEffect(() => {
        const savedGame = loadGame(saveId);

        if (!savedGame || savedGame.game.state.stateName === 'gameOver') {
            setIsLoading(false);
            return;
        }

        setGame(savedGame.game);
        setInitialAgentRequests(savedGame.agentRequests);
        setGameResetId((prev) => prev + 1);

        setIsLoading(false);
    }, [saveId]);

    if (!scenario) {
        notFound();
    }

    if (isLoading) {
        return (
            <div style={{ display: 'grid', placeItems: 'center', width: '100vw', height: '100vh' }}>
                <progress className="progress is-info" style={{ width: '300px' }}></progress>
            </div>
        );
    }

    function onStateChange(
        nextState: GameState,
        _request: StateRequestShape,
        remainingAgentRequests: StateRequestShape[]
    ) {
        saveGame(saveId, { ...game, state: nextState });
        saveAgentRequests(saveId, remainingAgentRequests);

        if (nextState.stateName !== 'gameOver') {
            return;
        }

        fetch('/api/game/stats', {
            method: 'PUT',
            headers: {
                'content-type': 'application/json',
            },
            body: JSON.stringify({
                scenarioId: game.scenarioId,
                winner: nextState.winner ?? null,
            }),
        });
    }

    function handleGameReset() {
        setGame(structuredClone(initialGame));
        setInitialAgentRequests([]);
        setGameResetId((prev) => prev + 1);
    }

    return (
        <GameContextProvider
            key={gameResetId}
            initialGame={game}
            scenario={scenario}
            loggedInUserId={userId}
        >
            <GameBoard
                strategyIngredients={strategyRecipeIngredients}
                onStateChange={onStateChange}
                navigationButton={<Navigation onReset={handleGameReset} />}
                initialAgentRequests={initialAgentRequests}
            />
        </GameContextProvider>
    );
}

function createGame(scenario: Scenario, gameId: number, userId: number) {
    const userSideId = SideId.A as SideId;
    const agentSideId: SideId = userSideId === SideId.A ? SideId.B : SideId.A;

    const gameState: GameState = {
        round: 1,
        units: scenario.units.map((unit, idx): Unit => {
            return {
                ...JSON.parse(JSON.stringify(unit)),
                id: idx + 1,
                maxMovementPoints: unit.movementPoints,
                isFatigued: false,
            };
        }),
        unitsSelectedForActivation: [],
        movingUnits: null,
        currentCombatId: null,
        activeSide: scenario.startingSide,
        stateName: 'selectingForActivation',
        victoryPoints: [
            { side: SideId.A, points: 0 },
            { side: SideId.B, points: 0 },
        ],
    };

    const currentTime = new Date();
    const month = `${currentTime.getMonth() + 1}`.padStart(2, '0');
    const day = `${currentTime.getDate()}`.padStart(2, '0');
    const hours = `${currentTime.getHours()}`.padStart(2, '0');
    const minutes = `${currentTime.getMinutes()}`.padStart(2, '0');
    const seconds = `${currentTime.getSeconds()}`.padStart(2, '0');

    const game: Game = {
        id: gameId,
        scenarioId: scenario.id,
        createdAt: `${currentTime.getFullYear()}-${month}-${day} ${hours}:${minutes}:${seconds}`,
        lastCommandId: 0,
        players: [
            {
                id: userId,
                username: 'You',
                side: userSideId,
            },
            {
                id: 0,
                username: 'Computer',
                side: agentSideId,
            },
        ],
        hexes: convertScenarioHexes(scenario.hexes),
        units: [],
        state: gameState,
        shouldShowShortCombatSummary: false,
    };

    return game;
}

function saveGame(saveId: string, game: Game) {
    const savedGame = loadGame(saveId);

    sessionStorage.setItem(
        saveId,
        JSON.stringify({ game, agentRequests: savedGame?.agentRequests ?? [] })
    );
}

function saveAgentRequests(saveId: string, agentRequests: StateRequestShape[]) {
    const savedGame = loadGame(saveId);

    if (!savedGame) {
        return;
    }

    sessionStorage.setItem(saveId, JSON.stringify({ game: savedGame.game, agentRequests }));
}

function loadGame(saveId: string) {
    const data = sessionStorage.getItem(saveId);

    if (data) {
        try {
            return JSON.parse(data) as {
                game: Game;
                agentRequests: StateRequestShape[];
            };
        } catch {
            return null;
        }
    }

    return null;
}

function convertScenarioHexes(scenarioHexes: (Hex | null)[][]) {
    const hexes: Hex[] = [];

    scenarioHexes.forEach((column) => {
        column.forEach((hex) => {
            if (!hex) {
                return;
            }

            hexes.push({
                ...hex,
            });
        });
    });

    return hexes;
}

export default GamePage;
